import { useLoading } from "context/LoadingContext";
import { ToastContext } from "context/ToastContext";
import { prepareCodesForSubmit, CodeRangeError } from "helpers/codeRanges";
import useDocumentTitle from "hooks/useDocumentTitle";
import { useContext, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useCreateInpatientRulesetMutation } from "store/queries/inpatientRulesets";
import { InpatientRulesetType } from "types/InpatientRuleset";

import {
  InpatientRulesetForm,
  IInpatientRulesetFormData,
  INITIAL_FORM_DATA,
} from "./components/InpatientRulesetForm";
import { buildInpatientOutlierConfig } from "./helpers/inpatientOutlier";

const VALID_CREATE_TYPES = new Set<string>([
  "FIXED_COST",
  "PASS_THROUGH",
  "EXCESSIVE_LOS",
  "TRANSFER",
  "STOP_LOSS",
  "CHARGE_OUTLIER",
  "INPATIENT_OUTLIER",
]);

function typeFromSearchParams(params: URLSearchParams): InpatientRulesetType {
  const raw = params.get("type")?.trim();
  const normalized = raw?.toUpperCase();
  if (normalized && VALID_CREATE_TYPES.has(normalized)) {
    return normalized as InpatientRulesetType;
  }
  return "FIXED_COST";
}

export function CreateInpatientRulesetPage() {
  useDocumentTitle("Create Inpatient Ruleset");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setIsLoading } = useLoading();
  const { current: toastElement } = useContext(ToastContext);
  const [createRuleset] = useCreateInpatientRulesetMutation();
  const [formData, setFormData] = useState<IInpatientRulesetFormData>(() => ({
    ...INITIAL_FORM_DATA,
    type: typeFromSearchParams(searchParams),
  }));

  const handleSubmit = async () => {
    let drgCodes: string[];
    try {
      drgCodes = prepareCodesForSubmit(formData.drgCodes);
    } catch (err) {
      toastElement?.show({
        severity: "error",
        summary: "Invalid code range",
        detail:
          err instanceof CodeRangeError
            ? err.message
            : "Failed to expand code ranges",
      });
      return;
    }
    setIsLoading(true);
    try {
      let config;
      if (formData.type === "FIXED_COST") {
        config = {
          costToChargeRatio: formData.costToChargeRatio!,
          fixedLossThresholdInCents: formData.fixedLossThresholdInCents!,
          outlierPercent: formData.outlierPercent!,
        };
      } else if (formData.type === "EXCESSIVE_LOS") {
        config = { formula: formData.formula };
      } else if (formData.type === "TRANSFER") {
        config = {
          losFormula: formData.losFormula,
          dischargeStatusCodes: formData.dischargeStatusCodes,
        };
      } else if (formData.type === "STOP_LOSS") {
        config = {
          thresholdMultiplier: formData.thresholdMultiplier!,
          paymentPercent: formData.paymentPercent!,
        };
      } else if (formData.type === "CHARGE_OUTLIER") {
        config = {
          thresholdType: formData.chargeOutlierThresholdType,
          ...(formData.chargeOutlierThresholdType === "FLAT"
            ? {
                thresholdAmountInCents:
                  formData.chargeOutlierThresholdAmountInCents!,
              }
            : {
                thresholdMultiplier: formData.chargeOutlierThresholdMultiplier!,
              }),
          outlierPercent: formData.chargeOutlierPercent!,
          ...(formData.chargeOutlierFloorInCents != null
            ? { chargeFloorInCents: formData.chargeOutlierFloorInCents }
            : {}),
        };
      } else if (formData.type === "INPATIENT_OUTLIER") {
        config = buildInpatientOutlierConfig(formData);
      } else {
        config = {
          thresholdInCents: formData.thresholdInCents!,
          chargeMultiplier: formData.chargeMultiplier!,
        };
      }

      const result = await createRuleset({
        providerId: formData.providerId!,
        payerId: formData.payerId!,
        name: formData.name.trim(),
        drgCodes,
        type: formData.type,
        config,
        contractLanguage: formData.contractLanguage.trim() || null,
        // CLE-2524: applicability wins over the legacy isDefault flag.
        applicability: formData.isDefault ? "TYPICAL" : "SCOPED",
      } as any).unwrap();

      if (result.warning) {
        toastElement?.show({
          severity: "warn",
          summary: "Warning",
          detail: result.warning,
          life: 5000,
        });
      }

      toastElement?.show({
        severity: "success",
        summary: "Created!",
        detail: "Inpatient ruleset created successfully.",
      });
      navigate(`/payer-rules`);
    } catch (error: any) {
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail: error?.data?.message || "Failed to create inpatient ruleset",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <InpatientRulesetForm
      title="Create Inpatient Ruleset"
      submitLabel="Create Inpatient Ruleset"
      submitIcon="pi pi-check"
      formData={formData}
      onChange={setFormData}
      onSubmit={handleSubmit}
    />
  );
}
